// -- UTM Parameter Capture & Storage ------------------------------------------
// Captures UTM params from the URL on first visit, persists them in
// sessionStorage so they survive SPA navigation within the same tab.

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
export type UtmKey = typeof UTM_KEYS[number];
export type UtmData = Partial<Record<UtmKey, string>>;

const SESSION_KEY = 'bbt_utm';

/** Call once on app mount. Reads URL params and writes to sessionStorage if found. */
export function captureUtmParams(): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const utm: UtmData = {};
  let found = false;
  for (const key of UTM_KEYS) {
    const val = params.get(key);
    if (val) { utm[key] = val; found = true; }
  }
  if (found) {
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(utm)); } catch { /* ok */ }
  }
}

/** Returns the most recently captured UTM data, or {} if none. */
export function getUtmParams(): UtmData {
  if (typeof window === 'undefined') return {};
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    return stored ? (JSON.parse(stored) as UtmData) : {};
  } catch {
    return {};
  }
}

/**
 * Formats UTM data into a human-readable string for inclusion in
 * WhatsApp lead messages.
 */
export function formatUtmForMessage(utm: UtmData): string {
  const parts: string[] = [];
  if (utm.utm_source)   parts.push(`Source: ${utm.utm_source}`);
  if (utm.utm_medium)   parts.push(`Medium: ${utm.utm_medium}`);
  if (utm.utm_campaign) parts.push(`Campaign: ${utm.utm_campaign}`);
  if (utm.utm_content)  parts.push(`Content: ${utm.utm_content}`);
  if (utm.utm_term)     parts.push(`Term: ${utm.utm_term}`);
  return parts.join(' | ');
}
